// System Design case studies, part 2: ride-hailing, notifications, file
// storage and sync, video streaming, and search autocomplete.

export const systemDesignCaseStudies2 = [
  {
    slug: 'design-ride-hailing',
    title: 'Case Study: Design a Ride-Hailing App',
    summary: 'Tracking millions of moving drivers, finding the nearest ones fast, and matching a ride without double-booking.',
    readTime: 13,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design the core of a ride-hailing app: a rider requests a ride and a nearby driver picks it up.” The interesting parts are a huge stream of location updates, geographic search, and making sure a ride is matched to exactly one driver.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Rider sees a fare estimate and requests a ride
  2. Nearby available drivers are offered the ride; one accepts
  3. Rider and driver see each other's live location during pickup and trip

Non-functional
  - Matching should feel quick: a driver offer within a few seconds
  - A ride is never assigned to two drivers, and a driver never gets two rides at once
  - Location data can be a few seconds stale

Out of scope: payments internals, ratings, surge pricing details` },

      { type: 'h2', text: '2. Estimates' },
      { type: 'code', language: 'text', code: `Active drivers:     ~500,000 at peak
Location updates:   every 4 seconds → ~125,000 writes/s
Ride requests:      ~20M/day ≈ 230/s average, ~1,000/s at peak
Insight: location updates dwarf everything else, and they are only
         useful for a few seconds → keep them in memory, not in the main DB` },

      { type: 'h2', text: '3. Entities and API' },
      { type: 'code', language: 'http', code: `Ride            { id, riderId, driverId?, pickup, dropoff, status, fare }
DriverLocation  { driverId, lat, lng, updatedAt, available }    (in memory)

POST /rides/estimate          { pickup, dropoff }       → { fare, etaMinutes }
POST /rides                   { pickup, dropoff }       → { rideId, status: "searching" }
PUT  /drivers/me/location     { lat, lng }              → 204   (every few seconds)
POST /rides/{id}/accept       (driver)                  → 200 | 409 already taken
GET  /rides/{id}              → status, driver location` },

      { type: 'h2', text: '4. High-level design' },
      {"type": "diagram", "title": "Ride-hailing: high-level design", "caption": "Location updates flow into an in-memory geo index partitioned by city. Ride requests are stored, then the matching service searches the index and sends an offer to the best driver.", "cols": 4, "rows": 3, "nodes": [{"id": "driver", "label": "Driver app", "kind": "client", "col": 0, "row": 0}, {"id": "loc", "label": "Location service", "kind": "service", "col": 1, "row": 0}, {"id": "geo", "label": "Geo index", "kind": "cache", "col": 2, "row": 0, "sub": "in memory, per city"}, {"id": "match", "label": "Matching service", "kind": "service", "col": 2, "row": 1, "sub": "ranks by ETA"}, {"id": "notify", "label": "Notification gateway", "kind": "edge", "col": 3, "row": 1, "sub": "push / WebSocket"}, {"id": "rider", "label": "Rider app", "kind": "client", "col": 0, "row": 2}, {"id": "ride", "label": "Ride service", "kind": "service", "col": 1, "row": 2}, {"id": "rides", "label": "Rides DB", "kind": "db", "col": 2, "row": 2}], "edges": [{"from": "driver", "to": "loc", "label": "every 4s"}, {"from": "loc", "to": "geo", "label": "latest position"}, {"from": "rider", "to": "ride", "label": "request ride"}, {"from": "ride", "to": "rides", "label": "save"}, {"from": "ride", "to": "match", "label": "find driver"}, {"from": "match", "to": "geo", "label": "nearby drivers"}, {"from": "match", "to": "notify", "label": "send offer"}]},

      { type: 'h2', text: '5. Deep dive: finding nearby drivers' },
      { type: 'p', text: 'Computing the distance from the rider to every driver in a city is far too slow. Instead, divide the map into cells and index drivers by cell.' },
      { type: 'table', headers: ['Approach', 'How it works', 'Notes'], rows: [
        ['Geohash', 'Encode lat/lng as a string; nearby points share a prefix', 'Simple; check neighbouring cells too because of cell edges'],
        ['Quadtree', 'Split dense areas into smaller squares recursively', 'Adapts to density; an in-memory tree'],
        ['Hexagonal grids (e.g. H3)', 'Cover the map with hexagons at several resolutions', 'Uniform neighbour distances; popular for ride-hailing'],
        ['Built-in geo queries', 'Redis GEO commands, PostGIS', 'Fast to build with']
      ] },
      { type: 'code', language: 'text', code: `Rider at cell X
  1. Look up drivers in cell X and its ring of neighbouring cells
  2. Keep only available drivers, sort by straight-line distance
  3. Ask a routing service for real driving ETAs for the closest ~10
  4. Offer the ride to the best candidate(s)` },

      { type: 'h2', text: '6. Deep dive: matching without double-booking' },
      { type: 'p', text: 'Two problems must never happen: two drivers both accepting the same ride, and one driver being matched to two rides. Both are contention problems, solved with atomic state changes.' },
      { type: 'code', language: 'sql', code: `-- Accept succeeds for exactly one driver
UPDATE rides SET driver_id = $1, status = 'accepted'
WHERE id = $2 AND status = 'searching';
-- 1 row updated → this driver won; 0 rows → someone else already accepted (409)` },
      { type: 'ul', items: [
        'While an offer is pending, mark the driver as “offered” with a short expiry (for example 15 seconds) so they are not offered another ride at the same time.',
        'If the driver declines or the offer expires, release the lock and offer the next candidate.',
        'Offer to one driver at a time for fairness, or to a few at once for speed, with the atomic update deciding the winner.'
      ] },

      { type: 'h2', text: '7. Deep dive: handling the location firehose' },
      { type: 'ul', items: [
        'Partition location data by city or region; cities are independent, which also isolates failures.',
        'Keep only the latest location per driver in memory; stream raw updates to cheap storage for analytics and disputes.',
        'During a trip, push the driver’s location to the rider over a persistent connection rather than having the rider poll.'
      ] },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'Basic flow, storing rides, the idea of searching for nearby drivers.'],
        ['Mid', 'A geospatial index, in-memory locations, real-time updates to the rider.'],
        ['Senior', 'Atomic matching and driver locks, partitioning by city, ETA-based ranking, failure handling for offers and connections.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Writing every location update to the main database.',
        'Scanning all drivers to compute distances.',
        'Check-then-set matching that lets two drivers accept one ride.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How would you add surge pricing?', answer: ['Continuously count ride requests and available drivers per cell over short windows in a stream processor, compute a multiplier when demand exceeds supply, and include it in fare estimates. Lock the quoted price for the rider for a few minutes.'] },
      { type: 'qa', question: 'What if the driver app loses connection mid-trip?', answer: ['The trip continues; the app buffers locations and sends them when it reconnects. The rider sees the last known position with a “reconnecting” state, and the server does not cancel trips based on short gaps.'] },

      { type: 'exercise', title: 'Design driver offers', task: 'Describe how offers are sent to drivers, what happens if a driver ignores an offer, and how you prevent a driver receiving two offers at once.', solution: `- Matching picks the best available driver and sets
  driver_state = "offered" with an expiry of ~15 s (atomic, in memory)
- The offer is pushed over the driver's open connection
- Accept → atomic UPDATE on the ride (status searching → accepted),
  driver_state → "on_trip"
- Decline or expiry → driver_state back to "available",
  matching offers the next candidate
- Drivers in "offered" or "on_trip" state are skipped by the geo search`, language: 'text' },

      { type: 'summary', items: [
        'Location updates are the dominant load: keep them in memory, partitioned by city.',
        'Use a geospatial index (geohash, quadtree, hexagons) to find nearby drivers.',
        'Atomic state changes prevent double-booking of rides and drivers.',
        'Push live updates over persistent connections.'
      ] }
    ]
  },
  {
    slug: 'design-notification-system',
    title: 'Case Study: Design a Notification System',
    summary: 'Sending push, email and SMS reliably at scale, with preferences, priorities, retries and no duplicates.',
    readTime: 11,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design a service that other teams use to send notifications by push, email and SMS.” The challenges are reliability, respecting user preferences, handling providers that fail, and avoiding both lost and duplicate messages.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Other services send a notification for a user and a template
  2. Deliver via push, email and/or SMS based on user preferences
  3. Respect opt-outs and quiet hours; support urgent messages (OTP, security)
  4. Track delivery status

Non-functional
  - Urgent messages within seconds; marketing can take longer
  - No lost notifications; duplicates kept to a minimum
  - Survive slow or failing third-party providers` },

      { type: 'h2', text: '2. Estimates' },
      { type: 'code', language: 'text', code: `Notifications:  ~100M/day ≈ 1,200/s average
Campaign peaks: a single marketing send to 20M users must not delay OTPs
Insight: separate priorities so bulk sends never block urgent messages` },

      { type: 'h2', text: '3. API' },
      { type: 'code', language: 'http', code: `POST /notifications
{
  "userId": "u_42",
  "template": "order_shipped",
  "data": { "orderId": "A1023" },
  "priority": "high",                       // high | normal | bulk
  "idempotencyKey": "order_shipped:A1023"
}
→ 202 { "notificationId": "n_981" }` },

      { type: 'h2', text: '4. High-level design' },
      {"type": "diagram", "title": "Notification system: high-level design", "caption": "The API validates and deduplicates requests, reads preferences, and routes each notification into a queue by priority. Channel workers send through providers and record delivery status.", "cols": 5, "rows": 3, "nodes": [{"id": "prod", "label": "Other services", "kind": "client", "col": 0, "row": 1}, {"id": "api", "label": "Notification API", "kind": "service", "col": 1, "row": 1, "sub": "dedupe, preferences"}, {"id": "prefs", "label": "Preferences", "kind": "db", "col": 1, "row": 0, "sub": "& templates"}, {"id": "status", "label": "Status store", "kind": "db", "col": 1, "row": 2}, {"id": "high", "label": "High priority", "kind": "queue", "col": 2, "row": 0}, {"id": "normal", "label": "Normal", "kind": "queue", "col": 2, "row": 1}, {"id": "bulk", "label": "Bulk", "kind": "queue", "col": 2, "row": 2}, {"id": "workers", "label": "Channel workers", "kind": "worker", "col": 3, "row": 1, "sub": "push · email · SMS"}, {"id": "p1", "label": "Push provider", "kind": "external", "col": 4, "row": 0}, {"id": "p2", "label": "Email provider", "kind": "external", "col": 4, "row": 1}, {"id": "p3", "label": "SMS provider", "kind": "external", "col": 4, "row": 2}], "edges": [{"from": "prod", "to": "api", "label": "notify"}, {"from": "api", "to": "prefs", "label": "read"}, {"from": "api", "to": "high"}, {"from": "api", "to": "normal"}, {"from": "api", "to": "bulk"}, {"from": "high", "to": "workers", "dashed": true}, {"from": "normal", "to": "workers", "dashed": true}, {"from": "bulk", "to": "workers", "dashed": true}, {"from": "workers", "to": "p1"}, {"from": "workers", "to": "p2"}, {"from": "workers", "to": "p3"}, {"from": "workers", "to": "status", "label": "status", "dashed": true, "labelAt": 0.7}]},

      { type: 'h2', text: '5. Deep dive: priorities and isolation' },
      { type: 'ul', items: [
        'Separate queues (or topics) per priority, with dedicated worker capacity for high priority.',
        'Separate workers per channel, so an email provider outage does not slow push notifications.',
        'Per-provider rate limits, matching what each provider allows.'
      ] },

      { type: 'h2', text: '6. Deep dive: reliability and duplicates' },
      { type: 'ul', items: [
        'The API stores the notification before acknowledging, then enqueues it (outbox pattern), so nothing is lost if a crash happens in between.',
        'Idempotency keys from producers collapse retries of the same event into one notification.',
        'Workers retry provider failures with exponential backoff; after several failures, messages go to a dead-letter queue.',
        'Record a “sent” status per notification and channel before retrying, so a worker restart does not resend messages already delivered.',
        'For critical messages, fall back to another channel or provider (for example SMS if push is not delivered).'
      ] },

      { type: 'h2', text: '7. Deep dive: user preferences' },
      { type: 'p', text: 'Check preferences at send time, not when the producer calls, because users can change them in between. Urgent security messages may ignore quiet hours but must still respect legal opt-outs for marketing. Store preferences in a fast store (cached) because every notification reads them.' },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'An API, queues, workers per channel, templates.'],
        ['Mid', 'Preferences, retries with backoff, delivery tracking.'],
        ['Senior', 'Priority isolation, idempotency end to end, provider failover, bulk campaigns without hurting urgent traffic.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'One shared queue where marketing blasts delay OTP codes.',
        'Calling providers synchronously inside the producer’s request.',
        'No deduplication, so retries send the same message several times.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you send a campaign to 20 million users?', answer: ['Expand the audience in batches in a background job, enqueue on the bulk priority queue, and throttle sending to provider limits. Urgent traffic keeps its own capacity, so it is unaffected.'] },
      { type: 'qa', question: 'How do you avoid spamming a user with many notifications?', answer: ['Add per-user rate limits and digests: batch low-priority notifications (for example, “5 new comments”) and send them together, and collapse duplicates for the same event.'] },

      { type: 'exercise', title: 'Make OTP delivery reliable', task: 'Login OTPs must arrive within 30 seconds. Describe the path, retries and fallbacks.', solution: `- High-priority queue with reserved workers; OTP never shares capacity
  with bulk sends
- Send via the primary SMS provider immediately; short timeout (~3 s)
- On failure or no delivery receipt within ~10 s, retry once via a
  second provider
- Offer "send via email" / voice call in the UI after 30 s
- Idempotency key per login attempt so retries never send two codes`, language: 'text' },

      { type: 'summary', items: [
        'Queue notifications by priority and channel to isolate failures.',
        'Store before acknowledging; deduplicate with idempotency keys.',
        'Retry with backoff, fall back to other providers for critical messages.',
        'Read preferences at send time.'
      ] }
    ]
  },
  {
    slug: 'design-file-storage-sync',
    title: 'Case Study: Design File Storage and Sync',
    summary: 'Uploading large files, syncing changes across devices, deduplication, versions and conflicts.',
    readTime: 12,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design a service like Google Drive or Dropbox where users store files and keep them in sync across devices.” The main ideas: separate file contents from metadata, transfer only what changed, and handle edits from several devices.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Upload, download and organise files in folders
  2. Changes on one device appear on the user's other devices
  3. Version history and restore
  4. Share files with other users

Non-functional
  - Never lose a file
  - Large files (many GB), resumable over unreliable networks
  - Efficient: don't re-upload a whole file for a small change` },

      { type: 'h2', text: '2. Core idea: metadata and blocks' },
      { type: 'p', text: 'Split every file into blocks (for example 4 MB), identify each block by a hash of its contents, and store blocks in object storage. The metadata database stores which blocks make up each version of each file.' },
      { type: 'code', language: 'text', code: `File      { id, ownerId, path, latestVersion }
Version   { fileId, version, blockHashes[], size, createdAt, deviceId }
Block     stored in object storage under its hash (content-addressed)` },

      { type: 'h2', text: '3. Upload and sync flow' },
      {"type": "diagram", "title": "File sync: upload and sync flow", "caption": "Device A uploads only the blocks the server does not already have, then commits a new version. The notifier tells Device B, which downloads just the blocks it is missing.", "cols": 4, "rows": 3, "nodes": [{"id": "a", "label": "Device A", "kind": "client", "col": 0, "row": 1}, {"id": "meta", "label": "Metadata service", "kind": "service", "col": 1, "row": 0, "sub": "versions & blocks"}, {"id": "metadb", "label": "Metadata DB", "kind": "db", "col": 2, "row": 0}, {"id": "blocks", "label": "Block storage", "kind": "storage", "col": 1, "row": 2, "sub": "content-addressed"}, {"id": "notify", "label": "Sync notifier", "kind": "edge", "col": 2, "row": 1, "sub": "long poll / WebSocket"}, {"id": "b", "label": "Device B", "kind": "client", "col": 3, "row": 1}], "edges": [{"from": "a", "to": "meta", "label": "1. commit version"}, {"from": "a", "to": "blocks", "label": "upload missing blocks"}, {"from": "meta", "to": "metadb", "label": "save"}, {"from": "meta", "to": "notify", "label": "2. changed", "dashed": true}, {"from": "notify", "to": "b", "label": "3. sync event"}, {"from": "b", "to": "blocks", "label": "4. download blocks"}]},
      { type: 'tip', text: 'Content-addressed blocks give deduplication for free: identical blocks across versions or files are stored once, and a small edit to a large file uploads only the changed blocks.' },

      { type: 'h2', text: '4. Deep dive: notifying devices' },
      { type: 'p', text: 'Each device keeps a cursor: the last change it has seen for the user. A long-poll or WebSocket connection tells devices that something changed; they then ask for “changes since my cursor”. This works the same way after a device has been offline for a week.' },

      { type: 'h2', text: '5. Deep dive: conflicts' },
      { type: 'p', text: 'If two devices both edit version 7 offline, the first to commit creates version 8. The second commit says “based on version 7”, which is no longer the latest, so the server rejects it as a conflict. The client keeps both: it saves its version as “report (conflicted copy).pdf” and lets the user decide.' },
      { type: 'callout', variant: 'note', title: 'Real-time co-editing is different', text: 'Documents edited by several people at the same moment (like an online doc editor) need operational transformation or CRDTs that merge individual edits. File sync works at the level of whole file versions.' },

      { type: 'h2', text: '6. Durability and sharing' },
      { type: 'ul', items: [
        'Object storage replicated across zones (or regions) for the blocks; a replicated database with backups for metadata.',
        'Blocks are deleted only when no version references them (reference counting or periodic garbage collection).',
        'Sharing adds access-control entries per file or folder; every download checks them and issues short-lived signed URLs.'
      ] },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'Object storage for files, a metadata database, upload and download APIs.'],
        ['Mid', 'Chunking, resumable uploads, notifying devices, version history.'],
        ['Senior', 'Content-addressed deduplication, cursors for sync, conflict handling, garbage collection, sharing permissions.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Storing file contents in the database.',
        'Re-uploading entire files on every change.',
        'Silently overwriting one device’s changes with another’s.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you make a 10 GB upload resumable?', answer: ['Upload blocks independently and record which ones the server has. After a disconnect, the client asks which blocks are missing and continues from there.'] },
      { type: 'qa', question: 'What is the downside of fixed-size blocks?', answer: ['Inserting a few bytes near the start shifts every later block boundary, so all following blocks change hash. Content-defined chunking chooses boundaries based on the data itself, so an insertion changes only nearby blocks.'] },

      { type: 'exercise', title: 'Support folder sharing', task: 'Users can share a folder with teammates as viewer or editor. Describe the data model and how permission checks work for files inside nested folders.', solution: `- Shares { resourceId (folder or file), granteeId, role, createdAt }
- To check access to a file: walk up its folder ancestors and look for a
  share that grants the user a role (cache the result per user + folder)
- Editors can commit new versions; viewers only download
- Downloads always go through the API, which checks permission and
  returns short-lived signed URLs for the blocks`, language: 'text' },

      { type: 'summary', items: [
        'Separate metadata (database) from contents (object storage blocks).',
        'Hash blocks to deduplicate and upload only what changed.',
        'Devices sync with cursors and change notifications.',
        'Detect conflicts with base versions and keep both copies.'
      ] }
    ]
  },
  {
    slug: 'design-video-streaming',
    title: 'Case Study: Design a Video Streaming Platform',
    summary: 'Uploading and transcoding video, adaptive streaming, CDNs, and counting views at scale.',
    readTime: 12,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design a platform like YouTube where creators upload videos and viewers watch them.” The heart of the problem is a processing pipeline for uploads and an efficient way to deliver huge amounts of video to viewers.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Creators upload videos; they become watchable after processing
  2. Viewers stream videos smoothly on any device and network
  3. View counts and basic metadata (title, description, channel)

Non-functional
  - Playback starts quickly and adapts to the viewer's bandwidth
  - Very read-heavy; video bandwidth dominates cost
  - Uploads can take minutes to process` },

      { type: 'h2', text: '2. Estimates' },
      { type: 'code', language: 'text', code: `Concurrent viewers:  ~2M at peak, average ~3 Mbps each
Egress:              ~6 Tbps        → impossible without a CDN
Uploads:             ~500,000/day; each stored in several resolutions` },

      { type: 'h2', text: '3. Upload and processing pipeline' },
      {"type": "diagram", "title": "Video platform: upload, processing and playback", "caption": "Uploads land in object storage and trigger a parallel transcoding pipeline. Viewers stream short segments through the CDN, and view events are aggregated asynchronously.", "cols": 4, "rows": 3, "nodes": [{"id": "creator", "label": "Creator", "kind": "client", "col": 0, "row": 0}, {"id": "raw", "label": "Raw uploads", "kind": "storage", "col": 1, "row": 0}, {"id": "q", "label": "Processing queue", "kind": "queue", "col": 2, "row": 0}, {"id": "tx", "label": "Transcoders", "kind": "worker", "col": 3, "row": 0, "sub": "parallel chunks"}, {"id": "seg", "label": "Segments + manifests", "kind": "storage", "col": 3, "row": 1}, {"id": "viewer", "label": "Viewer", "kind": "client", "col": 0, "row": 1}, {"id": "cdn", "label": "CDN", "kind": "edge", "col": 1, "row": 1, "sub": "edge cache"}, {"id": "vq", "label": "View events", "kind": "queue", "col": 1, "row": 2}, {"id": "agg", "label": "Aggregator", "kind": "worker", "col": 2, "row": 2}, {"id": "meta", "label": "Metadata & counts", "kind": "db", "col": 3, "row": 2}], "edges": [{"from": "creator", "to": "raw", "label": "pre-signed upload"}, {"from": "raw", "to": "q", "label": "uploaded", "dashed": true}, {"from": "q", "to": "tx", "dashed": true}, {"from": "tx", "to": "seg", "label": "write"}, {"from": "cdn", "to": "seg", "label": "origin fetch"}, {"from": "viewer", "to": "cdn", "label": "segments"}, {"from": "viewer", "to": "vq", "label": "views", "dashed": true}, {"from": "vq", "to": "agg", "dashed": true}, {"from": "agg", "to": "meta", "label": "counts"}]},
      { type: 'ul', items: [
        'Transcoding is CPU-heavy, so split long videos into pieces and transcode them in parallel on many workers.',
        'Produce lower resolutions first so the video becomes watchable sooner.',
        'Each step is idempotent and retryable; the video status moves through uploading → processing → ready (or failed).'
      ] },

      { type: 'h2', text: '4. Adaptive bitrate streaming' },
      { type: 'p', text: 'Each video is stored in several quality levels, each cut into short segments, with a manifest file listing them. The player downloads segments one by one and switches quality between segments based on measured bandwidth and how much video it has buffered. This is how streaming formats like HLS and DASH work.' },
      { type: 'tip', text: 'Segments are small, immutable files, which makes them perfect for CDN caching: popular videos are served almost entirely from edge locations near viewers.' },

      { type: 'h2', text: '5. Delivery through CDNs' },
      { type: 'ul', items: [
        'Popular videos are cached at the edge; the long tail is fetched from origin storage on demand.',
        'Pre-position new videos from very popular creators at edge locations before demand arrives.',
        'Use signed URLs for private or paid videos.'
      ] },

      { type: 'h2', text: '6. Counting views' },
      { type: 'p', text: 'The player sends view events to an event stream. A stream processor aggregates them per video per minute (filtering obvious bots and repeats) and updates counts in the metadata store. Counts displayed on the page can lag slightly; exact numbers are recomputed later from raw events.' },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'Object storage for videos, a metadata database, a CDN.'],
        ['Mid', 'Asynchronous transcoding, multiple resolutions, adaptive streaming.'],
        ['Senior', 'Parallel transcoding, edge strategy and cost, view counting pipeline, failure handling in processing.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Serving video from application servers.',
        'Transcoding synchronously during upload.',
        'One counter row updated on every view.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How would live streaming differ?', answer: ['There is no time for offline processing: the stream is ingested and transcoded in real time into short segments that are pushed to the CDN continuously, with a few seconds of delay. Capacity must be provisioned ahead of large live events.'] },
      { type: 'qa', question: 'Why split video into short segments?', answer: ['Segments let the player switch quality smoothly, start playback quickly without downloading the whole file, retry small pieces on network errors, and cache efficiently on CDNs.'] },

      { type: 'exercise', title: 'Speed up “time to watchable”', task: 'Creators complain that a 1-hour upload takes 40 minutes before anyone can watch it. Propose changes to the pipeline.', solution: `- Split the source into ~1-minute chunks and transcode chunks in parallel
- Produce 360p and 720p first; mark the video watchable at that point,
  add 1080p/4K later
- Prioritise creators with large audiences in the queue
- Autoscale transcoding workers on queue length
- Show the creator progress per step in their dashboard`, language: 'text' },

      { type: 'summary', items: [
        'Upload directly to storage; process asynchronously in a parallel pipeline.',
        'Store multiple qualities as short segments with manifests.',
        'Adaptive bitrate streaming plus CDNs make playback smooth and affordable.',
        'Count views through an event pipeline, not database counters.'
      ] }
    ]
  },
  {
    slug: 'design-search-autocomplete',
    title: 'Case Study: Design Search Autocomplete',
    summary: 'Suggesting completions in milliseconds: precomputed suggestions, tries, caching and keeping results fresh.',
    readTime: 10,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“As a user types in the search box, show the top suggestions.” The challenge is answering within tens of milliseconds for every keystroke, at very high request volume, with suggestions that reflect what people actually search for.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Return the top 5–10 suggestions for the typed prefix
  2. Rank by popularity, with recent trends reflected
  3. Filter offensive or unsafe suggestions

Non-functional
  - Very low latency: ~50 ms end to end
  - Very high volume: every keystroke is a request
  - Suggestions can be a little stale (minutes to hours)` },

      { type: 'h2', text: '2. Key insight: precompute' },
      { type: 'p', text: 'Ranking millions of queries on every keystroke is far too slow. Instead, compute the top suggestions for every prefix in advance, offline, and serve them with a single lookup.' },
      { type: 'code', variants: [
        { language: 'java', code: `import java.util.*;

public class Suggestions {
    // Build prefix → top-k suggestions from query counts (done offline)
    static Map<String, List<String>> build(Map<String, Integer> counts, int k, int maxPrefix) {
        Map<String, List<String>> table = new HashMap<>();
        for (String query : counts.keySet()) {
            for (int i = 1; i <= Math.min(query.length(), maxPrefix); i++) {
                table.computeIfAbsent(query.substring(0, i), p -> new ArrayList<>()).add(query);
            }
        }
        for (List<String> list : table.values()) {
            list.sort((a, b) -> counts.get(b) - counts.get(a));
            if (list.size() > k) list.subList(k, list.size()).clear();
        }
        return table;
    }

    public static void main(String[] args) {
        Map<String, Integer> counts = Map.of("react", 900, "react hooks", 500, "redux", 300,
                                             "react native", 450, "rust", 200);
        Map<String, List<String>> table = build(counts, 3, 6);
        System.out.println(table.get("re") + " " + table.get("ru"));
    }
}`, output: `[react, react hooks, react native] [rust]` },
        { language: 'python', code: `# Build prefix → top-k suggestions from query counts (done offline)
counts = {"react": 900, "react hooks": 500, "redux": 300, "react native": 450, "rust": 200}

def build_suggestions(counts, k=3, max_prefix=6):
    table = {}
    for query in counts:
        for i in range(1, min(len(query), max_prefix) + 1):
            table.setdefault(query[:i], []).append(query)
    return {p: sorted(qs, key=lambda q: -counts[q])[:k] for p, qs in table.items()}

suggestions = build_suggestions(counts)
print(suggestions["re"], suggestions["ru"])`, output: `['react', 'react hooks', 'react native'] ['rust']` },
        { language: 'javascript', code: `// Build prefix → top-k suggestions from query counts (done offline)
const counts = { "react": 900, "react hooks": 500, "redux": 300, "react native": 450, "rust": 200 };

function buildSuggestions(counts, k = 3, maxPrefix = 6) {
  const table = {};
  for (const [query, count] of Object.entries(counts)) {
    for (let i = 1; i <= Math.min(query.length, maxPrefix); i++) {
      (table[query.slice(0, i)] ??= []).push([query, count]);
    }
  }
  for (const prefix of Object.keys(table)) {
    table[prefix] = table[prefix].sort((a, b) => b[1] - a[1]).slice(0, k).map(([q]) => q);
  }
  return table;
}

const suggestions = buildSuggestions(counts);
console.log(suggestions["re"], suggestions["ru"]);`, output: `[ 'react', 'react hooks', 'react native' ] [ 'rust' ]` }
      ] },

      { type: 'h2', text: '3. Architecture' },
      {"type": "diagram", "title": "Autocomplete: offline build, online serving", "caption": "Top suggestions per prefix are computed offline from search logs and loaded into memory on suggestion servers. Requests are debounced in the browser and cached at the edge.", "cols": 4, "rows": 2, "nodes": [{"id": "logs", "label": "Search logs", "kind": "storage", "col": 0, "row": 0}, {"id": "agg", "label": "Aggregation job", "kind": "worker", "col": 1, "row": 0, "sub": "counts, time decay"}, {"id": "build", "label": "Prefix table build", "kind": "worker", "col": 2, "row": 0, "sub": "top-k per prefix"}, {"id": "snap", "label": "Table snapshot", "kind": "storage", "col": 3, "row": 0}, {"id": "browser", "label": "Browser", "kind": "client", "col": 0, "row": 1, "sub": "debounced"}, {"id": "cdn", "label": "CDN / edge cache", "kind": "edge", "col": 1, "row": 1}, {"id": "srv", "label": "Suggestion servers", "kind": "service", "col": 2, "row": 1, "sub": "table in memory"}], "edges": [{"from": "logs", "to": "agg", "dashed": true}, {"from": "agg", "to": "build", "dashed": true}, {"from": "build", "to": "snap", "label": "publish"}, {"from": "snap", "to": "srv", "label": "load"}, {"from": "browser", "to": "cdn", "label": "prefix"}, {"from": "cdn", "to": "srv", "label": "on miss"}]},
      { type: 'ul', items: [
        'Store the prefix table in memory on suggestion servers, or in a key-value store; a trie with top-k cached at each node is the classic structure.',
        'Limit prefix length (for example 15 characters); longer prefixes are rare and can be served from the closest shorter prefix.',
        'Shard by prefix if the table is too large for one machine; replicate for throughput.'
      ] },

      { type: 'h2', text: '4. Keeping it fast' },
      { type: 'ul', items: [
        'Cache responses at the CDN and in the browser: short prefixes are requested constantly and change rarely.',
        'Debounce keystrokes on the client and cancel outdated requests.',
        'When the user types one more character, filter the previous result list on the client while waiting for the server.'
      ] },

      { type: 'h2', text: '5. Freshness and trends' },
      { type: 'p', text: 'Rebuild the table periodically (hourly or daily) from aggregated counts, giving recent searches more weight. For breaking trends, a faster streaming pipeline can merge a small “trending” list into the results between rebuilds.' },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'A working prefix lookup and caching.'],
        ['Mid', 'Precomputed top-k per prefix, debouncing, offline pipeline.'],
        ['Senior', 'Trie or table sizing and sharding, freshness with time decay, edge caching, filtering and personalisation.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Running a database `LIKE` query on every keystroke.',
        'Ranking at request time.',
        'No client-side debouncing or caching.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How would you personalise suggestions?', answer: ['Blend the global top-k with the user’s own recent searches, either on the client or from a small per-user store, without rebuilding the global table per user.'] },
      { type: 'qa', question: 'How do you handle typos?', answer: ['Generate likely corrections offline for common misspellings, or use a search engine with fuzzy matching for longer queries where the precomputed table finds nothing.'] },

      { type: 'exercise', title: 'Size the table', task: 'You have 10 million distinct popular queries averaging 20 characters, with prefixes capped at 15 characters and top-5 suggestions per prefix. Estimate the number of prefixes in the worst case and suggest how to reduce it.', solution: `Worst case: 10M queries × 15 prefixes = 150M prefix entries,
but many prefixes are shared (e.g. "re" appears once), so the real
number is much smaller.
Reduce further:
- Drop queries below a minimum count
- Cap prefix length lower for rare prefixes
- Store suggestions as IDs into a query dictionary instead of strings`, language: 'text' },

      { type: 'summary', items: [
        'Precompute top suggestions per prefix offline; serve with one lookup.',
        'Cache at the edge and debounce on the client.',
        'Rebuild periodically with time decay; merge fast-moving trends.',
        'Filter unsafe suggestions before publishing.'
      ] }
    ]
  }
];
